/**
 * Routing a menu click through the launch sheet.
 *
 * `activateLeaf` decides what a menu entry means and, for anything that is
 * about to do real work, stops to show the sheet first. Plain navigation and
 * shell commands go straight through — putting a confirmation in front of
 * "open the viewer" would be noise, and noise is how confirmations stop being
 * read.
 */

import { create } from "zustand";

import { dispatchAction } from "@/shell/actions";
import { goToTab } from "@/pipeline/actions";
import { toolById } from "@/tools/catalog";
import { useToolSession } from "@/tools/session";
import { USE_CASES } from "@/usecases/catalog";
import { startUseCase } from "@/usecases/actions";
import type { NavLeaf } from "./nav.config";
import { targetFromTool, targetFromUseCase, type LaunchTarget } from "./launch";

interface Pending {
  target: LaunchTarget;
  /** The primary button's word: "Run", "Open", "Show". */
  verb: string;
  run: () => void | Promise<void>;
}

interface NavState {
  pending: Pending | null;
  busy: boolean;
  /** Per-session: the user ticked "don't show this again". */
  skipConfirm: boolean;
  setSkipConfirm: (value: boolean) => void;
  dismiss: () => void;
  confirm: () => Promise<void>;
  activateLeaf: (leaf: NavLeaf) => void;
}

export const useNavStore = create<NavState>((set, get) => ({
  pending: null,
  busy: false,
  skipConfirm: false,
  setSkipConfirm: (value) => set({ skipConfirm: value }),
  dismiss: () => set({ pending: null, busy: false }),

  confirm: async () => {
    const pending = get().pending;
    if (!pending) return;
    set({ busy: true });
    try {
      await pending.run();
    } finally {
      set({ pending: null, busy: false });
    }
  },

  activateLeaf: (leaf) => {
    // A raw shell command is navigation or a toggle; it does no work and needs
    // no confirming.
    if (leaf.action) {
      dispatchAction(leaf.action);
      return;
    }

    if (leaf.toolId) {
      const tool = toolById(leaf.toolId);
      if (!tool) return;
      const target = { ...targetFromTool(tool), title: leaf.label ?? tool.label };
      const run = () => {
        useToolSession.getState().selectTool(tool.id);
        goToTab("view");
        // SAM/MedSAM-style tools are neither a pipeline stage (`stage`) nor a
        // SimpleITK op (`op`) — there is nothing for `runSelected` to run.
        // They ask for a volume and then open their own interactive session,
        // so go straight to that picker (`ToolLaunchDialog`'s "Continue"
        // routes on to the actual panel from there).
        if (tool.interactive) useToolSession.getState().openLaunch();
      };
      // Opening a tool's panel is not yet running it, but the sheet is where
      // the requirements are legible, so anything that will need an engine or
      // a selection still goes through it.
      if (get().skipConfirm || (!target.needsEngine && !target.needsVolume)) {
        run();
        return;
      }
      set({ pending: { target, verb: "Open", run } });
      return;
    }

    if (leaf.useCaseId) {
      const useCase = USE_CASES.find((u) => u.id === leaf.useCaseId);
      if (!useCase) return;
      const target = { ...targetFromUseCase(useCase), title: leaf.label ?? useCase.title };
      const run = () => startUseCase(useCase);
      const isWork = useCase.action.kind === "pipeline" || useCase.action.kind === "batch";
      if (get().skipConfirm || !isWork) {
        run();
        return;
      }
      set({ pending: { target, verb: "Set up the run", run } });
    }
  },
}));
